import { useState, useCallback } from "react";

export type ConsentPreferences = {
  analytics: boolean;      // PostHog — behavior tracking & search events
  errorMonitoring: boolean; // Sentry — sendDefaultPii (IP, cookies, headers)
};

type StoredConsent = ConsentPreferences & {
  consentedAt: number; // Unix timestamp (ms)
};

const CONSENT_KEY = "cookie_consent";

// GDPR best practice: re-ask consent after 12 months
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

function readStored(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;

    // Migrate old string format from previous implementation
    if (raw === "accepted") return { analytics: true, errorMonitoring: true };
    if (raw === "rejected") return { analytics: false, errorMonitoring: false };

    const stored: StoredConsent = JSON.parse(raw);

    // Expire consent after 12 months — GDPR requires consent to remain current
    if (Date.now() - stored.consentedAt > CONSENT_EXPIRY_MS) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }

    return { analytics: stored.analytics, errorMonitoring: stored.errorMonitoring };
  } catch {
    return null;
  }
}

function writeStored(prefs: ConsentPreferences) {
  const stored: StoredConsent = { ...prefs, consentedAt: Date.now() };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
}

export function useCookieConsent() {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(readStored);

  const accept = useCallback(() => {
    const prefs = { analytics: true, errorMonitoring: true };
    writeStored(prefs);
    setPreferences(prefs);
  }, []);

  const reject = useCallback(() => {
    const prefs = { analytics: false, errorMonitoring: false };
    writeStored(prefs);
    setPreferences(prefs);
  }, []);

  const save = useCallback((prefs: ConsentPreferences) => {
    writeStored(prefs);
    setPreferences(prefs);
  }, []);

  // Clears the stored decision so the banner re-appears — required by GDPR
  // so users can withdraw or change consent at any time.
  const reset = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setPreferences(null);
  }, []);

  return { preferences, accept, reject, save, reset };
}
